import React from 'react';
import { FileText, Plus, Download, X, EyeOff, Eye, AlertTriangle } from 'lucide-react';

const AssignmentsSection = ({
  assignments,
  setShowAssignmentModal,
  handleDeleteAssignment,
  handleTakedownAssignment,
  handleRestoreAssignment,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Assignments</h1>
          <p className="text-gray-500">Create and manage homework assignments — auto-hide after deadline, restore or delete anytime.</p>
        </div>
        <button
          onClick={() => setShowAssignmentModal(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          data-testid="create-assignment-btn"
        >
          <Plus className="w-5 h-5" />
          Create Assignment
        </button>
      </div>

      {/* Assignment List */}
      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">All Assignments</h2>
          <p className="text-xs text-gray-500 mt-1">
            Expired (past deadline) and Taken-down assignments are automatically hidden from mentees but remain here for review and deletion.
          </p>
        </div>
        <div className="divide-y">
          {assignments.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No assignments created yet. Click "Create Assignment" to add one.
            </div>
          ) : (
            assignments.map((assignment) => {
              const isExpired = Boolean(assignment.expired);
              const isTakenDown = Boolean(assignment.taken_down);
              const isHidden = isExpired || isTakenDown;
              return (
                <div
                  key={assignment.id}
                  data-testid={`assignment-row-${assignment.id}`}
                  className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${isHidden ? 'bg-gray-50' : ''}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isHidden ? 'bg-gray-200' : 'bg-purple-100'}`}>
                      <FileText className={`w-5 h-5 ${isHidden ? 'text-gray-500' : 'text-purple-600'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`font-medium truncate ${isHidden ? 'text-gray-600' : 'text-gray-900'}`}>
                        {assignment.title}
                        {assignment.attached_file && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            <Download className="w-3 h-3" />
                            File
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        Deadline: {new Date(assignment.deadline).toLocaleDateString()}
                        <span className="mx-2">•</span>
                        {assignment.category}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Status pills */}
                    {isTakenDown && (
                      <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                        <EyeOff className="w-3 h-3" />
                        Taken down
                      </span>
                    )}
                    {!isTakenDown && isExpired && (
                      <span className="px-2 py-1 text-xs rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Expired
                      </span>
                    )}
                    {!isHidden && (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                        Active
                      </span>
                    )}

                    {/* Action buttons */}
                    {isTakenDown ? (
                      <button
                        onClick={() => handleRestoreAssignment(assignment.id)}
                        data-testid={`restore-assignment-${assignment.id}`}
                        title="Restore — make visible to mentees again"
                        className="px-3 py-1.5 text-xs rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Restore
                      </button>
                    ) : (
                      <button
                        onClick={() => handleTakedownAssignment(assignment.id)}
                        data-testid={`takedown-assignment-${assignment.id}`}
                        title="Take down — hide from mentees but keep the record"
                        className="px-3 py-1.5 text-xs rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1"
                      >
                        <EyeOff className="w-3.5 h-3.5" />
                        Take down
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteAssignment(assignment.id)}
                      data-testid={`delete-assignment-${assignment.id}`}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Delete forever"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentsSection;
